import { cloneDeep, get, keyBy, maxBy } from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import {
  defaultNodeStaticProps,
  LOAD_SOFT_CONSTRAINT_NODE_TYPE,
  NODES_DESCRIPTION,
  POWER_SOFT_CONSTRAINT_NODE_TYPE,
  ROOT_NODE_TYPE,
  VIRTUAL_NODES,
  CHARGERS_BY_ADDRESS_NODE_TYPE,
  CHARGER_NODE_TYPE,
} from '../constants';
import { getZoomCof, isResourceNodeType } from '../SchemaDisplay/helpers/node';
import { getAvailablePhases } from './NodeForm/helper';
import { PLAZA_TEMPLATE_TYPES } from '../../helper';

export const makeNode = (nodeType, additionalProps = {}) => {
  const { id, ...rest } = additionalProps;
  const plazaTmpl = get(rest, 'plazaSettings.templateType');
  const isSoftConstraint = [LOAD_SOFT_CONSTRAINT_NODE_TYPE, POWER_SOFT_CONSTRAINT_NODE_TYPE].includes(nodeType);
  const isAllowedTmpl = [PLAZA_TEMPLATE_TYPES.CurrentBased, PLAZA_TEMPLATE_TYPES.DemandBased].includes(plazaTmpl);

  if (isSoftConstraint && isAllowedTmpl) {
    const optimizationAlgorithm = nodeType === LOAD_SOFT_CONSTRAINT_NODE_TYPE ? 'EQUAL_AMOUNT_OF_LOAD' : 'EQUAL_AMOUNT_OF_POWER';

    rest.optimization_algorithm = optimizationAlgorithm;
  }

  return {
    id: id || uuidv4(),
    staticSection: defaultNodeStaticProps[nodeType](rest),
    topologyNodeType: nodeType,
  };
};

export const calculateNodeSchemaPosition = (start, end, nodeRect, schemaRect, zoomCof) => {
  const nodeLeft = nodeRect.left ?? nodeRect.x ?? 0;
  const nodeTop = nodeRect.top ?? nodeRect.y ?? 0;
  const schemaLeft = schemaRect.left ?? schemaRect.x ?? 0;
  const schemaTop = schemaRect.top ?? schemaRect.y ?? 0;

  const innerIntent = { x: start.x - nodeLeft, y: start.y - nodeTop };
  const left = (end.x - innerIntent.x - schemaLeft) * (zoomCof.x || 1);
  const top = (end.y - innerIntent.y - schemaTop) * (zoomCof.y || 1);

  return {
    positionX: left >= 0 ? Math.round(left) : 0,
    positionY: top >= 0 ? Math.round(top) : 0,
  };
};

const getStartY = (nodes, targetY) => {
  const defaultY = targetY;
  const maxNode = maxBy(nodes, (node) => get(node, 'staticSection.positionY', defaultY));
  const maxY = maxNode ? get(maxNode, 'staticSection.positionY', defaultY) : defaultY;

  return maxY + 480;
};

export const calculateChargersPositions = (nodes, targetConstraintId, chargersCount) => {
  const CHARGER_WIDTH = 235;
  const CHARGER_HEIGHT = 480;
  const COLUMN_GAP = 30;
  const ROW_GAP = 30;
  const MAX_CHARGERS_PER_ROW = 10;
  const HORIZONTAL_STEP = CHARGER_WIDTH + COLUMN_GAP;
  const VERTICAL_STEP = CHARGER_HEIGHT + ROW_GAP;

  const targetNode = nodes.find((node) => node.id === targetConstraintId);
  const startX = targetNode ? (targetNode.staticSection && targetNode.staticSection.positionX) || 300 : 300;

  const targetY = targetNode ? (targetNode.staticSection && targetNode.staticSection.positionY) || 200 : 200;
  const startY = getStartY(nodes, targetY) + ROW_GAP;

  const positions = [];
  for (let index = 0; index < chargersCount; index += 1) {
    const row = Math.floor(index / MAX_CHARGERS_PER_ROW);
    const col = index % MAX_CHARGERS_PER_ROW;
    positions.push({
      positionX: Math.round(startX + col * HORIZONTAL_STEP),
      positionY: Math.round(startY + row * VERTICAL_STEP),
    });
  }

  return positions;
};

export const makeNotExistString = (index, str, collection) => {
  if (collection.indexOf(`${str} ${index}`) === -1) {
    return `${str} ${index}`;
  }

  return makeNotExistString(index + 1, str, collection);
};

export const makeNodeName = ({ nodes, nodeType }) => {
  const nodeName = NODES_DESCRIPTION[nodeType].title;
  const existNames = nodes.map((node) => node.staticSection.nodeName);

  if (existNames.indexOf(nodeName) !== -1) {
    return makeNotExistString(1, nodeName, existNames);
  }

  return nodeName;
};

export const makeDroppedNodeHandler = (props) => (event) => {
  const {
    createLocalNode,
    createLocalResourceNode,
    plazaId,
    nodes,
    schemaParams,
    setEditableNodeId,
    plazaSettings,
    lockPlazaEditor,
    openAddChargersModal,
  } = props;

  if (event.dataTransfer.getData('newNode') === '') {
    return false;
  }

  const { nodeType, start, targetRect } = JSON.parse(event.dataTransfer.getData('newNode'));

  if (nodeType === CHARGERS_BY_ADDRESS_NODE_TYPE) {
    if (openAddChargersModal) {
      openAddChargersModal();
    }

    return true;
  }

  const nodeName = makeNodeName({ nodes, nodeType });
  const isRoot = nodeType === ROOT_NODE_TYPE;

  let node;

  const schemaRect =
    event.currentTarget && typeof event.currentTarget.getBoundingClientRect === 'function'
      ? event.currentTarget.getBoundingClientRect()
      : event.target.getBoundingClientRect();

  const position = calculateNodeSchemaPosition(
    start,
    { x: event.clientX, y: event.clientY },
    targetRect,
    schemaRect,
    getZoomCof(schemaParams),
  );

  if (isResourceNodeType(nodeType)) {
    node = makeNode(nodeType, { ...position, nodeName, isNew: true });
    createLocalResourceNode(node);
  } else {
    node = makeNode(nodeType, {
      id: isRoot ? plazaId : undefined,
      ...position,
      nodeName,
      isNew: true,
      plazaSettings,
    });
    createLocalNode(node);
  }
  lockPlazaEditor();
  setEditableNodeId(node.id);
};

export const appendNodeRecursively = (item, nodeId, parentNodeId, nodeChildren = []) => {
  const { id, children } = item;

  if (id === parentNodeId) {
    const child = children.find((child) => child.id === nodeId);

    if (!child) children.push({ id: nodeId, children: nodeChildren });
  } else if (children.length > 0) {
    children.forEach((child) => appendNodeRecursively(child, nodeId, parentNodeId, nodeChildren));
  }
};

export const removeNodeRecursively = (item, nodeId) => {
  const { children } = item;

  item.children = children.filter((child) => child.id !== nodeId);

  if (children.length > 0) {
    children.forEach((child) => removeNodeRecursively(child, nodeId));
  }
};

export const moveThroughHierarchy = (hierarchy, call) => {
  const { id, children } = hierarchy;

  if (children && children.length) {
    children.forEach((child) => {
      call({ parent: id, child: child.id, children });
      moveThroughHierarchy(child, call);
    });
  }
};

export const findTopologyNode = (hierarchy, nodeId) => {
  let node = null;

  hierarchy.children.forEach((child) => {
    if (!node && child.id === nodeId) {
      node = cloneDeep(child);
    } else if (!node) {
      node = findTopologyNode(child, nodeId);
    }
  });

  return node;
};

export const isPlacedIntoHierarchy = (hierarchy, nodeId) => {
  let parentId;

  moveThroughHierarchy(hierarchy, ({ parent, child }) => {
    if (child === nodeId) parentId = parent;
  });

  return parentId;
};

export const appendTopologyNode = (topology, nodeId, parentNodeId) => {
  const hierarchy = cloneDeep(topology);
  const placedNodeParentId = isPlacedIntoHierarchy(hierarchy, nodeId);

  if (placedNodeParentId) {
    const topologyNode = findTopologyNode(hierarchy, nodeId);

    removeNodeRecursively(hierarchy, nodeId);
    appendNodeRecursively(hierarchy, nodeId, parentNodeId, topologyNode.children);
  } else {
    appendNodeRecursively(hierarchy, nodeId, parentNodeId);
  }

  return hierarchy;
};

export const removeTopologyNode = (topology, nodeId) => {
  const hierarchy = cloneDeep(topology);

  removeNodeRecursively(hierarchy, nodeId);

  return hierarchy;
};

export const makeConnectChecker = (hierarchy) => (nodeId) => {
  let connected = false;

  moveThroughHierarchy(hierarchy, ({ parent, child }) => {
    if (parent === nodeId || child === nodeId) connected = true;
  });

  return connected;
};

export const makeChildrenChecker = (hierarchy) => (nodeId) => {
  let hasChildren = false;

  moveThroughHierarchy(hierarchy, ({ parent, child }) => {
    if (parent === nodeId && child) hasChildren = true;
  });

  return hasChildren;
};

/**
 * Updating phases configuration
 * This method mutates nodes
 * @param hierarchy
 * @param nodes
 * @returns []
 */
export const updatePhases = (hierarchy = {}, nodes = []) => {
  const nodesByKey = keyBy(nodes, 'id');

  moveThroughHierarchy(hierarchy, ({ parent, child }) => {
    const parentNode = nodesByKey[parent];
    const childNode = nodesByKey[child];

    if (!childNode) return;

    const parentPhaseCount = getAvailablePhases(parentNode);
    const parentPhaseConnectivity = Object.keys(get(parentNode, 'staticSection.phases.absoluteRotation', {})).length;
    const childPhaseConnectivity = Object.keys(get(childNode, 'staticSection.phases.relativeRotation', {})).length;
    const childRelativeRotation = Object.values(get(childNode, 'staticSection.phases.relativeRotation', {}));

    if (VIRTUAL_NODES.includes(childNode.topologyNodeType)) {
      // Copying parent config for proxy nodes which have no the phases controls
      childNode.staticSection.phases = parentNode.staticSection.phases;
    } else {
      // Reducing child phase count depends on the parent nodes
      childRelativeRotation.forEach((phase) => {
        if (Number(phase) > parentPhaseConnectivity) {
          if (parentPhaseCount > 1) {
            if (childPhaseConnectivity >= parentPhaseConnectivity) {
              childNode.staticSection.phases.relativeRotation = { 1: '1', 2: '2' };
              childNode.staticSection.phaseConnectivityType = 2;
            } else {
              childNode.staticSection.phases.relativeRotation = { 1: '1' };
              childNode.staticSection.phaseConnectivityType = 1;
            }
          } else {
            childNode.staticSection.phases.relativeRotation = { 1: '1' };
            childNode.staticSection.phaseConnectivityType = 1;
          }
        }
      });

      // Resetting absolute rotation
      if (childNode.staticSection.phases) {
        childNode.staticSection.phases.absoluteRotation = {};

        // Setting absolute rotation depending on the parent one
        Object.keys(childNode.staticSection.phases.relativeRotation).forEach((key) => {
          const childRelativeReference = childNode.staticSection.phases.relativeRotation[key];

          childNode.staticSection.phases.absoluteRotation[key] = parentNode.staticSection.phases.absoluteRotation[childRelativeReference];
        });
      }
    }
  });

  return nodes;
};
